import SearchInput from "@/components/common/search-input";
import NoData from "@/components/ui/no-data";
import { Skeleton } from "@/components/ui/skeleton";
import SortDropdown from "@/pages/procesos/components/SortDropdown";
import {
  useGetFCTables,
  type Table,
} from "@/queries/data-center/functional-core.api";
import {
  sortDirectionEnum,
  type SortDirectionType,
  type SortOptionsTypes,
} from "@/types/common/filters";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  RowCard,
} from "@elaniintc/goes-design-system";
import { parseAsString, useQueryState } from "nuqs";
import { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router";
import { useDebounceValue } from "usehooks-ts";

const sortFields: SortOptionsTypes[] = [
  { label: "Nombre", value: "name" },
  // { label: "Estado", value: "trashed" },
  // { label: "Fecha de actualización", value: "updated_at" },
  // { label: "Fecha de creación", value: "created_at" },
];

//TODO: update or remove since dates are not available
// const filterFields: FilterTypeList[] = [
//   {
//     label: "Fecha de creación",
//     type: filterTypesEnum.dates,
//     field: "created_at",
//   },
//   {
//     label: "Última Actualización",
//     type: filterTypesEnum.dates,
//     field: "updated_at",
//   },
//   {
//     label: "Estado",
//     type: filterTypesEnum.status,
//     field: "status",
//     options: [
//       { label: "Activos", field: "ACTIVE" },
//       { label: "Inactivo", field: "INACTIVE" },
//     ],
//   },
// ];

export const FunctionalCore = () => {
  const [orderField, setOrderField] = useState<string | undefined>("name");
  const [query, setQuery] = useQueryState("search", parseAsString);
  const [searchInput, setSearchInput] = useState(query || "");
  const [deferredQuery] = useDebounceValue(searchInput || "", 500);
  const [orderDirection, setOrderDirection] = useState<SortDirectionType>(
    sortDirectionEnum.asc,
  );
  const { data: dataTables, isLoading, isFetching } = useGetFCTables();

  const filteredRows = useMemo(() => {
    if (!dataTables) return [];
    return dataTables.data
      .map((row, originalIndex) => ({ ...row, originalIndex }))
      .sort((a: Table, b: Table) => {
        if (!orderField) return 0;
        if (orderField !== "name") return 0;
        if (a.name < b.name) return orderDirection === "asc" ? -1 : 1;
        if (a.name > b.name) return orderDirection === "asc" ? 1 : -1;
        return 0;
      })
      .filter((row) => {
        return Object.values(row).some((value) =>
          String(value).toLowerCase().includes(deferredQuery.toLowerCase()),
        );
      });
  }, [deferredQuery, orderField, orderDirection, dataTables]);

  const handleSort = (
    field: string | undefined,
    direction: SortDirectionType,
  ) => {
    setOrderField(field);
    setOrderDirection(direction);
  };

  useEffect(() => {
    setQuery(deferredQuery || null);
  }, [deferredQuery, setQuery]);

  if (isFetching && searchInput.length === 0) {
    return <ScreenSkeleton />;
  }

  return (
    <div className="w-full" data-testid="functional-core-page">
      {dataTables?.data.length === 0 && !isLoading ? (
        <div className="flex flex-col justify-center items-center min-h-[calc(100vh-210px)]">
          <NoData
            title=" Aún no existen tablas"
            description="Vuelve más tarde para verificar la existencia de nuevas tablas."
          />
        </div>
      ) : (
        <div className="flex flex-col gap-8 pl-2 pt-8">
          <p className="font-normal text-body leading-3xl pr-2">
            Administrar y visualizar las tablas contenidas en el centro de
            datos.
          </p>
          <div className="flex flex-col gap-4">
            <div className="flex justify-between pr-2">
              <div className="flex space-x-3">
                <SortDropdown
                  currentOrderField={orderField}
                  currentOrderDirection={orderDirection}
                  sortOptions={sortFields}
                  onClick={handleSort}
                />
                {/* 
                  TODO: show when filter data is available
                  <Filter
                    filters={filterFields}
                    currentFilters={filters}
                    onCurrentFiltersChange={(e) => {
                      setFilters(e);
                      setPage(1);
                    }}
                  /> */}
              </div>
              <div className="flex flex-column gap-2">
                <SearchInput
                  data-testid="search-input-dc-core"
                  variant="xs"
                  placeholder="Buscar tablas..."
                  className="h-9 w-[240px] text-sm"
                  value={searchInput || ""}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
            </div>
            <div className="flex flex-col gap-4 flex-auto">
              {filteredRows.length > 0 ? (
                <div className="flex flex-col gap-2 pr-2.5 w-full">
                  {filteredRows.map((table) => (
                    <NavLink
                      key={table.id}
                      to={`/centro-de-datos/core-funcional/${table.name}`}
                    >
                      <RowCard
                        title={table.name}
                        subtitle=""
                        // subtitle={`Última actualización ${formatToRelativeTime(table.updated_at)}  |  Creado el ${formatDateToCommaString(table.created_at)}`}
                        // TODO: update or remove description field
                        icon={{ name: "table" }}
                      >
                        <RowCard.Right>
                          {/* <Badge
                            size="sm"
                            className="rounded-lg"
                            shouldHover={false}
                            variant="neutral"
                          >
                            Operador
                          </Badge> 
                          // TODO: update or remove owner field
                          */}

                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="xs"
                                className="size-8 cursor-pointer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Icon
                                  type="far"
                                  name="ellipsis-vertical"
                                  className="text-current"
                                />
                                <span className="sr-only">
                                  Acciones para tabla
                                </span>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              className="w-[222px] min-w-56 rounded-lg border-neutral text-body font-normal p-2 font-geist"
                              side="left"
                              align="start"
                            >
                              <DropdownMenuItem
                                className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
                                asChild
                              >
                                <NavLink
                                  to={`/centro-de-datos/core-funcional/${table.name}`}
                                  className="w-full"
                                >
                                  Ver tabla
                                </NavLink>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </RowCard.Right>
                      </RowCard>
                    </NavLink>
                  ))}
                </div>
              ) : (
                <div className="w-full flex justify-center items-center h-[calc(100vh-390px)]">
                  <h2 className="text-heading-quiet font-semibold text-2xl">
                    Sin resultados
                  </h2>
                </div>
              )}
              {/* Footer TODO: replace with TablePagination component */}
              <div className="w-full flex items-center">
                <p className="text-sm text-body leading-2xl">
                  Mostrando{" "}
                  <span className="font-semibold">{filteredRows.length}</span>{" "}
                  de{" "}
                  <span className="font-semibold">
                    {dataTables?.data?.length || 0}
                  </span>{" "}
                  resultados.
                </p>
                <div className="size-8"></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TablesSkeleton = () => {
  return (
    <div className="w-full flex flex-col mt-2 gap-2">
      {Array.from({ length: 5 }).map((_, idx) => (
        <Skeleton
          key={idx + "skl"}
          className="w-full gap-4 h-[68px] rounded-xl px-4 py-3"
        />
      ))}
    </div>
  );
};

const ScreenSkeleton = () => {
  return (
    <div className="w-full flex flex-col space-y-2 p-2 pt-8">
      <Skeleton className="h-5 w-[418px]" />
      <div className="flex justify-between pt-10">
        <div className="flex space-x-3">
          <Skeleton className="h-9 w-[118px]" />
          <Skeleton className="h-9 w-[79px]" />
        </div>
        <div className="flex space-x-3">
          <Skeleton className="h-9 w-[238px]" />
          <Skeleton className="h-9 w-[138px]" />
        </div>
      </div>
      <TablesSkeleton />
    </div>
  );
};
